import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Grid,
  Divider
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import api from '../../../api/axiosInterceptor';
import TableComponent from '../../../Components/TableComponent/TableComponent';
import { SnackBarComponent } from '../../../Components/snackBar/snackBar';
import { LoadingSpinner } from '../../../Components/spinner/spinner';

export const AdminWarrantCaseWorkerComponent = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [userList, setUserList] = useState([]);

  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const [formData, setFormData] = useState({
    allowed_user: '',
    is_summons_creation_permission: false,
    is_warrant_creation_permission: false,
    is_publication_permission: false,
    is_asset_valuation_permission: false,
    register_of_seized_movable_property_permission: false,
    categorization_of_seized_movable_property_permission: false,
    is_active: true,
  });

  const [formErrors, setFormErrors] = useState({});
  const [userSearch, setUserSearch] = useState('');

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  const resetForm = () => {
    setFormData({
      allowed_user: '',
      is_summons_creation_permission: false,
      is_warrant_creation_permission: false,
      is_publication_permission: false,
      is_asset_valuation_permission: false,
      register_of_seized_movable_property_permission: false,
      categorization_of_seized_movable_property_permission: false,
      is_active: true,
    });
    setFormErrors({});
    setUserSearch('');
  };

  const fetchWarrantCaseWorkerList = async () => {
    setLoading(true);
    try {
      const [caseWorkerRes, userRes] = await Promise.all([
        api.get(`/fe-mgmt/warrant-case-worker/`),
        api.get(`/auth/role-mgmt/`),
      ]);

      setUserList(userRes.data || []);

      const userMap = new Map((userRes.data || []).map(u => [u.id, u.email]));

      const enrichedRows = (caseWorkerRes.data || []).map((item, index) => ({
        id: item.id,
        slNo: index + 1,
        allowed_user: item.allowed_user,
        is_summons_creation_permission: !!item.is_summons_creation_permission,
        is_warrant_creation_permission: !!item.is_warrant_creation_permission,
        is_publication_permission: !!item.is_publication_permission,
        is_asset_valuation_permission: !!item.is_asset_valuation_permission,
        register_of_seized_movable_property_permission: !!item.register_of_seized_movable_property_permission,
        categorization_of_seized_movable_property_permission: !!item.categorization_of_seized_movable_property_permission,
        is_active: !!item.is_active,
        case_worker_email: userMap.get(item.allowed_user) || 'N/A',
      }));

      setRows(enrichedRows);
    } catch (error) {
      setSnackbarMessage('Failed to fetch Warrant Case Worker records.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserList = () => {
    api.get(`/auth/role-mgmt/`)
      .then(res => setUserList(res.data || []))
      .catch(() => setUserList([]));
  };

  useEffect(() => {
    fetchWarrantCaseWorkerList();
    fetchUserList();
  }, []);

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleAdd = () => {
    setIsEditMode(false);
    setSelectedItem(null);
    resetForm();
    setOpenFormDialog(true);
  };

  const handleEdit = (item) => {
    setIsEditMode(true);
    setSelectedItem(item);
    setFormData({
      allowed_user: item.allowed_user || '',
      is_summons_creation_permission: !!item.is_summons_creation_permission,
      is_warrant_creation_permission: !!item.is_warrant_creation_permission,
      is_publication_permission: !!item.is_publication_permission,
      is_asset_valuation_permission: !!item.is_asset_valuation_permission,
      register_of_seized_movable_property_permission: !!item.register_of_seized_movable_property_permission,
      categorization_of_seized_movable_property_permission: !!item.categorization_of_seized_movable_property_permission,
      is_active: !!item.is_active,
    });
    setFormErrors({});
    setUserSearch('');
    setOpenFormDialog(true);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.allowed_user) errors.allowed_user = 'Case Worker is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      if (isEditMode) {
        await api.patch(`/fe-mgmt/warrant-case-worker/${selectedItem.id}/`, formData);
        setSnackbarMessage('Warrant Case Worker record updated successfully.');
      } else {
        await api.post(`/fe-mgmt/warrant-case-worker/`, formData);
        setSnackbarMessage('Warrant Case Worker record created successfully.');
      }
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setOpenFormDialog(false);
      fetchWarrantCaseWorkerList();
    } catch (error) {
      setSnackbarMessage('Failed to save Warrant Case Worker record.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleDeleteConfirmation = (item) => {
    setDeleteItem(item);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/fe-mgmt/warrant-case-worker/${deleteItem.id}/`);
      setSnackbarMessage('Warrant Case Worker record deleted successfully.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      fetchWarrantCaseWorkerList();
    } catch (error) {
      setSnackbarMessage('Failed to delete record.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  const filteredUsers = userList.filter(u =>
    u.email && u.email.toLowerCase().includes(userSearch.toLowerCase())
  );

  const columns = [
    { id: 'slNo', field: 'slNo', headerName: 'Sl No.', width: 70 },
    {
      id: 'allowed_user',
      field: 'allowed_user',
      headerName: 'Case Worker Email',
      width: 250,
      renderCell: (params) => {
        const user = userList.find(u => u.id === params.value);
        return user ? user.email : 'N/A';
      },
    },
    // {
    //   id: 'is_summons_creation_permission',
    //   field: 'is_summons_creation_permission',
    //   headerName: 'Summons',
    //   width: 95,
    //   renderCell: (params) => (params.value ? 'Yes' : 'No'),
    // },
    {
      id: 'is_warrant_creation_permission',
      field: 'is_warrant_creation_permission',
      headerName: 'Warrant',
      width: 95,
      renderCell: (params) => (params.value ? 'Yes' : 'No'),
    },
    // {
    //   id: 'is_publication_permission',
    //   field: 'is_publication_permission',
    //   headerName: 'Publication',
    //   width: 105,
    //   renderCell: (params) => (params.value ? 'Yes' : 'No'),
    // },
    // {
    //   id: 'is_asset_valuation_permission',
    //   field: 'is_asset_valuation_permission',
    //   headerName: 'Asset Val.',
    //   width: 100,
    //   renderCell: (params) => (params.value ? 'Yes' : 'No'),
    // },
    // {
    //   id: 'register_of_seized_movable_property_permission',
    //   field: 'register_of_seized_movable_property_permission',
    //   headerName: 'Register Movable',
    //   width: 130,
    //   renderCell: (params) => (params.value ? 'Yes' : 'No'),
    // },
    // {
    //   id: 'categorization_of_seized_movable_property_permission',
    //   field: 'categorization_of_seized_movable_property_permission',
    //   headerName: 'Categorization',
    //   width: 120,
    //   renderCell: (params) => (params.value ? 'Yes' : 'No'),
    // },
    {
      id: 'is_active',
      field: 'is_active',
      headerName: 'Active',
      width: 80,
      renderCell: (params) => (params.value ? 'Yes' : 'No'),
    },
    {
      id: 'actions',
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      renderCell: (params) => (
        <>
          <IconButton color="primary" onClick={() => handleEdit(params.row)} disabled={loading}>
            <Edit />
          </IconButton>
          <IconButton color="error" onClick={() => handleDeleteConfirmation(params.row)} disabled={loading}>
            <Delete />
          </IconButton>
        </>
      ),
    },
  ];

  if (loading) return <LoadingSpinner size={30} />;

  return (
    <Box sx={{ width: '100%', mt: 8, mb: 1, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', bgcolor: '#fff' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" bgcolor="rgba(255,228,196,0.1)" p={2}>
        <Typography variant="h4" fontWeight="bold">Warrant Case Worker Master</Typography>
        <Button variant="contained" color="primary" onClick={handleAdd}>
          Add New Case Worker
        </Button>
      </Box>

      <TableComponent
        columns={columns}
        rows={rows}
        manageColumnStatus
        enableSort
        sx={{
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#ff8c00',
            color: '#fff',
            fontWeight: 'bold',
          },
        }}
      />

      <Dialog open={openFormDialog} onClose={() => setOpenFormDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{isEditMode ? 'Edit Warrant Case Worker' : 'Add Warrant Case Worker'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Search Case Worker by Email"
            margin="normal"
            fullWidth
            value={userSearch}
            onChange={e => setUserSearch(e.target.value)}
            placeholder="Type to search email..."
          />

          <TextField
            select
            label="Assign Case Worker *"
            margin="normal"
            fullWidth
            value={formData.allowed_user}
            onChange={e => setFormData({ ...formData, allowed_user: e.target.value })}
            error={!!formErrors.allowed_user}
            helperText={formErrors.allowed_user}
          >
            {filteredUsers.length ? (
              filteredUsers.map(user => (
                <MenuItem key={user.id} value={user.id}>
                  {user.email}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>No matching users</MenuItem>
            )}
          </TextField>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            Permissions
          </Typography>

          <Grid container spacing={1}>
            {/* <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.is_summons_creation_permission}
                    onChange={e =>
                      setFormData({ ...formData, is_summons_creation_permission: e.target.checked })
                    }
                  />
                }
                label="Summons Creation Permission"
              />
            </Grid> */}

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.is_warrant_creation_permission}
                    onChange={e =>
                      setFormData({ ...formData, is_warrant_creation_permission: e.target.checked })
                    }
                  />
                }
                label="Warrant Creation Permission"
              />
            </Grid>

            {/* <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.is_publication_permission}
                    onChange={e =>
                      setFormData({ ...formData, is_publication_permission: e.target.checked })
                    }
                  />
                }
                label="Publication Permission"
              />
            </Grid> */}

            {/* <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.is_asset_valuation_permission}
                    onChange={e =>
                      setFormData({ ...formData, is_asset_valuation_permission: e.target.checked })
                    }
                  />
                }
                label="Asset Valuation Permission"
              />
            </Grid> */}

            {/* <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.register_of_seized_movable_property_permission}
                    onChange={e =>
                      setFormData({
                        ...formData,
                        register_of_seized_movable_property_permission: e.target.checked,
                      })
                    }
                  />
                }
                label="Register of Seized Movable Property Permission"
              />
            </Grid> */}

            {/* <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.categorization_of_seized_movable_property_permission}
                    onChange={e =>
                      setFormData({
                        ...formData,
                        categorization_of_seized_movable_property_permission: e.target.checked,
                      })
                    }
                  />
                }
                label="Categorization of Seized Movable Property Permission"
              />
            </Grid> */}

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.is_active}
                    onChange={e => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenFormDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {isEditMode ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this warrant case worker record?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <SnackBarComponent
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
      />
    </Box>
  );
};